import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { VerifiedBadge } from "@/components/Badges";
import {
  ArrowLeft,
  MapPin,
  Globe,
  Instagram,
  Briefcase,
  Star,
  Loader2,
  Calendar,
  Share2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { labelCreatorType } from "@/lib/roleCategories";
import ReportButton from "@/components/ReportButton";
import ConnectButton from "@/components/ConnectButton";

/** Resolve a stored image reference into a full URL.
 *  Supports legacy http(s) URLs (seed data) and new storage paths uploaded via /api/pros/me/gallery. */
function resolveImage(p) {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  return `${API}/img/${p}?w=1600&q=86`;
}

export default function CreatorPublicProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    api
      .get(`/creators/${id}`)
      .then(({ data }) => setData(data))
      .catch((err) => {
        const status = err?.response?.status;
        setError(status === 404 ? "Créateur introuvable" : "Impossible de charger ce profil");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: data?.profile?.company_name || data?.user?.name || "Créateur", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien copié");
      }
    } catch {
      /* user cancelled */
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-32" data-testid="creator-profile-loading">
          <Loader2 className="w-6 h-6 text-[#C68E59] animate-spin" />
        </div>
      </AppShell>
    );
  }

  if (error || !data) {
    return (
      <AppShell>
        <div className="max-w-xl mx-auto py-24 text-center" data-testid="creator-profile-error">
          <h2 className="font-serif text-3xl text-[#1A1A1A]">{error || "Profil introuvable"}</h2>
          <p className="text-sm text-[#666] mt-3">Ce créateur n'existe pas ou son profil a été retiré.</p>
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="mt-6 rounded-full border-[#C68E59] text-[#6E4B2A] hover:bg-[#FAF3EA]"
            data-testid="creator-profile-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </AppShell>
    );
  }

  const { user, profile, collaborations = [], reliability = {} } = data;
  const gallery = (profile.gallery_images || []).filter(Boolean);
  const displayName = profile.company_name || user.name || "Créateur";
  const initials = displayName[0]?.toUpperCase() || "?";
  const igHandle = profile.instagram?.replace(/^@/, "");
  const websiteHref = profile.website && !/^https?:\/\//i.test(profile.website) ? `https://${profile.website}` : profile.website;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto" data-testid="creator-profile-page">
        {/* Top nav */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-[#6E4B2A] hover:text-[#1A1A1A] transition-colors"
            data-testid="creator-profile-back-top"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <button
            onClick={share}
            className="inline-flex items-center gap-2 text-sm text-[#6E4B2A] hover:text-[#1A1A1A] transition-colors"
            data-testid="creator-profile-share"
          >
            <Share2 className="w-4 h-4" />
            Partager
          </button>
        </div>

        {/* Report button (logged-in users) */}
        <div className="flex justify-end items-center gap-3 mb-2">
          <ConnectButton userId={user.user_id} className="!h-10 !px-5" />
          <ReportButton targetType="creator" targetId={user.user_id} />
        </div>

        {/* Hero card */}
        <div className="bg-white rounded-3xl border border-[#EADDC9] p-6 sm:p-10 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            {profile.logo_url || user.picture ? (
              <img
                src={profile.logo_url || user.picture}
                alt=""
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl object-cover border border-[#EADDC9] flex-shrink-0"
              />
            ) : (
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-[#FAF3EA] flex items-center justify-center font-serif text-4xl text-[#A26F3E] border border-[#EADDC9] flex-shrink-0">
                {initials}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <p className="caption-label text-[#C68E59]">Créateur Helencia</p>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <h1 className="font-serif text-3xl sm:text-5xl text-[#1A1A1A] leading-tight">{displayName}</h1>
                {profile.verified && <VerifiedBadge />}
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-[#666]">
                {profile.pro_type && (
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase className="w-3.5 h-3.5 text-[#A26F3E]" />
                    {labelCreatorType(profile.pro_type)}
                  </span>
                )}
                {profile.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-[#A26F3E]" />
                    {profile.location}
                  </span>
                )}
                {reliability.avg_rating > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5 text-[#C68E59] fill-[#C68E59]" />
                    {reliability.avg_rating.toFixed(1)}
                    <span className="text-[#8B8680]">({reliability.review_count})</span>
                  </span>
                )}
              </div>

              {/* Project types as chips */}
              {profile.project_types?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {profile.project_types.map((p) => (
                    <span
                      key={p}
                      className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-[#F5E6D0] text-[#8B5A2B]"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              )}

              {/* External links */}
              {(websiteHref || igHandle) && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {websiteHref && (
                    <a
                      href={websiteHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#D4C3A5] text-[#1A1A1A] hover:border-[#1A1A1A] transition-colors"
                      data-testid="creator-website-link"
                    >
                      <Globe className="w-3.5 h-3.5" />
                      Site web
                    </a>
                  )}
                  {igHandle && (
                    <a
                      href={`https://instagram.com/${igHandle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#D4C3A5] text-[#1A1A1A] hover:border-[#1A1A1A] transition-colors"
                      data-testid="creator-instagram-link"
                    >
                      <Instagram className="w-3.5 h-3.5" />
                      @{igHandle}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {profile.description && (
            <div className="mt-8 pt-8 border-t border-[#FAF3EA]">
              <p className="caption-label text-[#A26F3E]">À propos</p>
              <p className="text-[#1A1A1A] leading-relaxed mt-3 whitespace-pre-line">{profile.description}</p>
            </div>
          )}
        </div>

        {/* Gallery */}
        {gallery.length > 0 && (
          <div className="mt-10" data-testid="creator-gallery">
            <div className="mb-5">
              <p className="caption-label text-[#C68E59]">Galerie</p>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Univers visuel</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="creator-gallery-grid">
              {gallery.map((p, i) => (
                <button
                  key={`${p}-${i}`}
                  type="button"
                  onClick={() => setLightbox(i)}
                  className="group relative aspect-[4/5] overflow-hidden rounded-2xl border border-[#EADDC9] bg-[#FAF3EA] focus:outline-none focus:ring-2 focus:ring-[#C68E59] focus:ring-offset-2"
                  data-testid={`creator-gallery-thumb-${i}`}
                >
                  <img
                    src={resolveImage(p)}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Active collaborations */}
        <div className="mt-10">
          <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
            <div>
              <p className="caption-label text-[#C68E59]">Projets ouverts</p>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mt-1">Collaborations en cours</h2>
            </div>
            {collaborations.length > 0 && (
              <Link
                to="/collaborations"
                className="text-sm text-[#6E4B2A] hover:text-[#1A1A1A] transition-colors"
                data-testid="creator-all-collabs-link"
              >
                Voir toutes les collaborations →
              </Link>
            )}
          </div>

          {collaborations.length === 0 ? (
            <div className="bg-white rounded-3xl border border-[#EADDC9] p-10 text-center" data-testid="creator-collabs-empty">
              <p className="font-serif text-xl text-[#1A1A1A]">Aucune collaboration ouverte pour le moment</p>
              <p className="text-sm text-[#666] mt-2">Revenez bientôt — les nouveaux projets apparaîtront ici.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="creator-collabs-grid">
              {collaborations.map((c) => (
                <Link
                  key={c.id}
                  to={`/collaborations/${c.id}`}
                  className="group bg-white rounded-3xl border border-[#EADDC9] p-6 hover:border-[#C68E59]/50 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] transition-all flex flex-col"
                  data-testid={`creator-collab-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-serif text-lg text-[#1A1A1A] line-clamp-2 group-hover:text-[#A26F3E] transition-colors">
                      {c.title || "Collaboration"}
                    </h3>
                    {c.paid && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#1A1A1A] text-white whitespace-nowrap">
                        Rémunéré
                      </span>
                    )}
                  </div>
                  {c.description && <p className="text-sm text-[#666] mt-2 line-clamp-3">{c.description}</p>}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-4 text-xs text-[#8B8680]">
                    {c.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {c.location}
                      </span>
                    )}
                    {c.date && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {new Date(c.date).toLocaleDateString("fr-FR")}
                      </span>
                    )}
                    {c.project_type && <span>{c.project_type}</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && gallery[lightbox] && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 sm:p-8"
          onClick={() => setLightbox(null)}
          data-testid="creator-gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm"
            aria-label="Fermer"
            data-testid="creator-gallery-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={resolveImage(gallery[lightbox])}
            alt=""
            className="max-w-full max-h-full rounded-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {gallery.length > 1 && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-wider">
              {lightbox + 1} / {gallery.length}
            </p>
          )}
        </div>
      )}
    </AppShell>
  );
}
